import { useEffect, useRef, useState } from "react";
import { router, usePoll } from "@inertiajs/react";
import { Loader2, Plus } from "lucide-react";
import DropdownMenu from "@/components/menus/DropdownMenu";
import ConfirmDialog from "@/components/modals/ConfirmDialog";
import ImageLightbox from "@/components/modals/ImageLightbox";

// Shape sent by the backend via the top-level `artwork` Inertia prop.
// Lives separately from the `track` prop so that polling for variant
// readiness never disturbs other page state (e.g. form fields on Edit).
// The `uploaded` boolean lives on the track itself (`track.artwork_attached`)
// since it's a fact about the track, not something polling needs to refresh.
export interface Artwork {
  thumbnail_url: string | null;
  original_url: string | null;
}

export interface ArtworkSlotProps {
  trackId: number;
  uploaded: boolean;
  thumbnailUrl: string | null;
  originalUrl: string | null;
  alt?: string;
}

const slotClasses =
  "size-14 shrink-0 overflow-hidden rounded-xl md:size-16";

// Mounted only while the variant is being processed by the worker.
// usePoll triggers a partial reload of the `artwork` prop every 2s; when the
// variant becomes ready the parent stops rendering this component and the
// hook tears down automatically.
const ArtworkPoller: React.FC = () => {
  usePoll(2000, { only: ["artwork"] });
  return null;
};

const ArtworkSlot: React.FC<ArtworkSlotProps> = ({
  trackId,
  uploaded,
  thumbnailUrl,
  originalUrl,
  alt,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isWindowDragging, setIsWindowDragging] = useState(false);

  const isProcessing = uploaded && !thumbnailUrl;
  const showPlaceholder = !thumbnailUrl && !isProcessing;

  useEffect(() => {
    if (!showPlaceholder) return;

    let resetTimer: ReturnType<typeof setTimeout>;

    const handleWindowDragOver = (e: DragEvent) => {
      if (!Array.from(e.dataTransfer?.types ?? []).includes("Files")) return;
      setIsWindowDragging(true);
      clearTimeout(resetTimer);
      resetTimer = setTimeout(() => setIsWindowDragging(false), 100);
    };
    const handleWindowDrop = () => setIsWindowDragging(false);

    window.addEventListener("dragover", handleWindowDragOver);
    window.addEventListener("drop", handleWindowDrop);
    return () => {
      window.removeEventListener("dragover", handleWindowDragOver);
      window.removeEventListener("drop", handleWindowDrop);
      clearTimeout(resetTimer);
    };
  }, [showPlaceholder]);

  const openFilePicker = () => fileInputRef.current?.click();

  const uploadFile = (file: File) => {
    router.patch(
      `/tracks/${trackId}/artwork`,
      { artwork: file },
      { forceFormData: true, preserveScroll: true },
    );
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    uploadFile(file);
    e.target.value = "";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  };

  const handleRemove = () => {
    setRemoving(true);
    router.delete(`/tracks/${trackId}/artwork`, {
      preserveScroll: true,
      onFinish: () => {
        setRemoving(false);
        setShowRemoveModal(false);
      },
    });
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileSelect}
        className="hidden"
      />

      {thumbnailUrl ? (
        <DropdownMenu
          items={[
            { text: "View", onClick: () => setShowLightbox(true) },
            { text: "Replace artwork", onClick: openFilePicker },
            ...(originalUrl
              ? [{ text: "Download", href: originalUrl, download: true }]
              : []),
            {
              text: "Remove",
              onClick: () => setShowRemoveModal(true),
              variant: "danger" as const,
            },
          ]}
          renderTrigger={({ ref, props }) => (
            <button
              ref={ref}
              {...props}
              type="button"
              aria-label="Artwork actions"
              className={`group relative ${slotClasses}`}
            >
              <img
                src={thumbnailUrl}
                alt={alt ?? "Artwork"}
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/20" />
            </button>
          )}
        />
      ) : isProcessing ? (
        <div
          className={`${slotClasses} bg-peach-100 dark:bg-peach-900 text-peach-900 dark:text-peach-100 flex items-center justify-center`}
        >
          <ArtworkPoller />
          <Loader2 size={20} className="animate-spin" />
        </div>
      ) : (
        <button
          type="button"
          onClick={openFilePicker}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          aria-label="Add artwork"
          className={`${slotClasses} text-peach-900 dark:text-peach-100 flex items-center justify-center border-2 border-dashed transition-colors ${
            isDragging
              ? "bg-peach-200 dark:bg-peach-800"
              : "bg-peach-100 hover:bg-peach-200 dark:bg-peach-900 dark:hover:bg-peach-800"
          } ${
            isWindowDragging || isDragging
              ? "border-peach-500"
              : "border-transparent"
          }`}
        >
          <Plus size={24} />
        </button>
      )}

      <ConfirmDialog
        isOpen={showRemoveModal}
        onClose={() => setShowRemoveModal(false)}
        onConfirm={handleRemove}
        title="Remove artwork"
        message="Are you sure you want to remove this artwork? You can upload a new one anytime."
        confirmText="Remove"
        processing={removing}
      />

      {(originalUrl ?? thumbnailUrl) && (
        <ImageLightbox
          isOpen={showLightbox}
          src={(originalUrl ?? thumbnailUrl)!}
          alt={alt ?? "Artwork"}
          onClose={() => setShowLightbox(false)}
        />
      )}
    </>
  );
};

export default ArtworkSlot;
